import React from 'react';
import {
    ChangeCoachFailure,
    ChangeCoachFailureReason,
    getChangeCoachErrorMessage,
    getChangeCoachFailureReason,
} from '../../lib/changeCoachError';

export type ChangeCoachInquiryInfo = {
    memberName: string;
    memberId: string;
    centerId: string;
    occurredAt: string;
    errorCode?: string;
};

type Props = {
    failure: ChangeCoachFailure;
    isRetrying?: boolean;
    inquiryInfo?: ChangeCoachInquiryInfo;
    onRetry?: () => void;
    onRefresh?: () => void;
    onViewMemberships?: () => void;
};

const REASON_PRESENTATION: Record<
    ChangeCoachFailureReason,
    { title: string; tone: 'warning' | 'error' }
> = {
    share_owner: { title: '공유를 먼저 정리해 주세요', tone: 'warning' },
    not_connected: { title: '회원 정보가 달라졌어요', tone: 'warning' },
    transfer_conflict: { title: '운영팀 확인이 필요해요', tone: 'error' },
    refund_in_progress: { title: '환불이 끝난 뒤 바꿀 수 있어요', tone: 'warning' },
    refund_review_required: { title: '관리자 확인이 필요해요', tone: 'warning' },
    permission: { title: '변경 권한이 없어요', tone: 'error' },
    network: { title: '연결 상태를 확인해 주세요', tone: 'error' },
    blocked: { title: '담당 강사를 바꿀 수 없어요', tone: 'error' },
    other: { title: '담당 강사를 바꿀 수 없어요', tone: 'error' },
};

const buttonStyle = (tone: 'warning' | 'error'): React.CSSProperties => ({
    minWidth: 104,
    height: 38,
    padding: '0 16px',
    borderRadius: 8,
    border: `1px solid ${tone === 'warning' ? '#FDBA74' : '#FCA5A5'}`,
    backgroundColor: tone === 'warning' ? '#C2410C' : '#B91C1C',
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
});

const ChangeCoachFailureCard = ({
    failure,
    isRetrying = false,
    inquiryInfo,
    onRetry,
    onRefresh,
    onViewMemberships,
}: Props) => {
    const [copyState, setCopyState] = React.useState<'idle' | 'copied' | 'failed'>('idle');
    const reason = getChangeCoachFailureReason(failure);
    const presentation = REASON_PRESENTATION[reason];
    const isWarning = presentation.tone === 'warning';
    const reasonMessage =
        reason === 'transfer_conflict'
            ? '수강권 상태를 확인해야 담당 강사를 변경할 수 있어요.'
            : getChangeCoachErrorMessage(failure);

    React.useEffect(() => {
        setCopyState('idle');
    }, [failure]);

    const copyInquiryInfo = async () => {
        if (!inquiryInfo) return;
        const text = [
            `회원 이름: ${inquiryInfo.memberName}`,
            `회원 ID: ${inquiryInfo.memberId}`,
            `센터 ID: ${inquiryInfo.centerId}`,
            `발생 시각: ${inquiryInfo.occurredAt}`,
            `오류 코드: ${inquiryInfo.errorCode || '확인 불가'}`,
        ].join('\n');

        try {
            await navigator.clipboard.writeText(text);
            setCopyState('copied');
            return;
        } catch {
            // 권한·비보안 환경이면 아래 대체 방식으로 한 번 더 시도한다.
        }
        let copied = false;
        const textarea = document.createElement('textarea');
        try {
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            copied = document.execCommand('copy');
        } catch {
            copied = false;
        } finally {
            if (textarea.parentNode) textarea.parentNode.removeChild(textarea);
        }
        setCopyState(copied ? 'copied' : 'failed');
    };

    const renderActions = () => {
        if (reason === 'share_owner') {
            return onViewMemberships ? (
                <button
                    type="button"
                    style={buttonStyle(presentation.tone)}
                    onClick={onViewMemberships}
                >
                    수강권 보기
                </button>
            ) : null;
        }
        if (reason === 'not_connected' && onRefresh) {
            return (
                <button type="button" style={buttonStyle(presentation.tone)} onClick={onRefresh}>
                    최신 정보로 새로고침
                </button>
            );
        }
        if (reason === 'transfer_conflict') {
            return inquiryInfo ? (
                <button
                    type="button"
                    style={buttonStyle(presentation.tone)}
                    onClick={() => void copyInquiryInfo()}
                >
                    {copyState === 'copied'
                        ? '복사됐어요'
                        : copyState === 'failed'
                          ? '복사하지 못했어요 · 다시 시도'
                          : '문의 정보 복사'}
                </button>
            ) : null;
        }
        if ((reason === 'network' || reason === 'other') && onRetry) {
            return (
                <button
                    type="button"
                    disabled={isRetrying}
                    style={{
                        ...buttonStyle(presentation.tone),
                        cursor: isRetrying ? 'not-allowed' : 'pointer',
                        opacity: isRetrying ? 0.55 : 1,
                    }}
                    onClick={onRetry}
                >
                    {isRetrying ? '다시 시도 중...' : '다시 시도'}
                </button>
            );
        }
        return null;
    };

    const actions = renderActions();

    return (
        <section
            role="alert"
            aria-live="polite"
            style={{
                width: '100%',
                padding: '18px 20px',
                borderRadius: 12,
                border: `1px solid ${isWarning ? '#FED7AA' : '#FECACA'}`,
                backgroundColor: isWarning ? '#FFF7ED' : '#FEF2F2',
                color: '#374151',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span
                    aria-hidden="true"
                    style={{
                        flex: '0 0 auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: 999,
                        backgroundColor: isWarning ? '#F97316' : '#EF4444',
                        color: '#FFFFFF',
                        fontSize: 16,
                        fontWeight: 800,
                    }}
                >
                    !
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <h3 style={{ margin: 0, color: '#111827', fontSize: 17, lineHeight: '26px', fontWeight: 800 }}>
                        {presentation.title}
                    </h3>
                    <p style={{ margin: '6px 0 0', color: '#4B5563', fontSize: 14, lineHeight: '21px' }}>
                        {reasonMessage}
                    </p>

                    {reason === 'share_owner' && (
                        <div style={{ marginTop: 14 }}>
                            <strong style={{ display: 'block', color: '#7C2D12', fontSize: 13, marginBottom: 7 }}>
                                이렇게 해 주세요
                            </strong>
                            <ol
                                style={{
                                    margin: 0,
                                    paddingLeft: 20,
                                    color: '#4B5563',
                                    fontSize: 13,
                                    lineHeight: '21px',
                                }}
                            >
                                <li>공유 참여자의 예약을 정리해요.</li>
                                <li>수강권 공유를 종료해요.</li>
                                <li>담당 강사 변경을 다시 시도해요.</li>
                            </ol>
                        </div>
                    )}

                    {reason === 'transfer_conflict' && (
                        <p style={{ margin: '10px 0 0', color: '#7F1D1D', fontSize: 13, lineHeight: '20px' }}>
                            라포 운영팀에 문의해 주세요. 확인에 필요한 정보를 아래 버튼으로 복사할 수 있어요.
                        </p>
                    )}

                    {actions && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 8,
                                marginTop: 16,
                                flexWrap: 'wrap',
                            }}
                        >
                            {actions}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ChangeCoachFailureCard;
